// Pages/CashierAssistancePage.jsx
//
// Employee portal view of cashier-assistance tickets.
// A ticket is created automatically when the store button is pressed
// (store + time + PENDING). Employees fill in the rest here:
// what the issue was, who resolved it, and the final status.
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCashierTicketsThunk, updateCashierTicketThunk } from '../features/cashierAssistance/cashierThunks';
import { fetchEmployeesThunk } from '../features/employees/employeeThunks';
import Sidebar from '../Component/Sidebar';

const STATUS_COLORS = {
  PENDING: '#f59e0b',
  IN_PROGRESS: '#3b82f6',
  RESOLVED: '#10b981',
  FALSE_ALARM: '#6b7280',
};

// Shimmering placeholder shown while tickets load. Mirrors the real ticket
// card layout (colored edge, title line, subtitle line, action button) so
// the page doesn't jump when data arrives.
const shimmerCss = `
@keyframes cashier-shimmer {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}`;

const SkeletonBar = ({ w, h = 12, mt = 0 }) => (
  <div style={{
    width: w, height: h, marginTop: mt, borderRadius: 6,
    background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
    backgroundSize: '800px 100%',
    animation: 'cashier-shimmer 1.4s linear infinite',
  }} />
);

const SkeletonTicketCard = () => (
  <div style={{ border: '1px solid #e5e7eb', borderLeft: '6px solid #e2e8f0',
                padding: '14px 18px', display: 'flex',
                justifyContent: 'space-between', alignItems: 'center' }}>
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <SkeletonBar w={190} h={14} />
        <SkeletonBar w={70} h={18} />
      </div>
      <SkeletonBar w={230} h={11} mt={8} />
    </div>
    <SkeletonBar w={78} h={34} />
  </div>
);

const SkeletonList = ({ count = 4 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <style>{shimmerCss}</style>
    {Array.from({ length: count }, (_, i) => <SkeletonTicketCard key={i} />)}
  </div>
);

const CashierAssistancePage = () => {
  const dispatch = useDispatch();
  const { tickets, loading, error } = useSelector((s) => s.cashierTickets || { tickets: [] });
  const { employees = [] } = useSelector((s) => s.employees || {});
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null); // ticket being edited
  const [form, setForm] = useState({ issue_description: '', resolved_by: '', status: '' });

  const load = useCallback(() => {
    dispatch(fetchCashierTicketsThunk(statusFilter ? { status: statusFilter } : {}));
  }, [dispatch, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (employees.length === 0) dispatch(fetchEmployeesThunk());
  }, [dispatch, employees.length]);

  // Refresh every 30s so PENDING tickets appear even without WS wiring
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const openEdit = (ticket) => {
    setEditing(ticket);
    setForm({
      issue_description: ticket.issue_description || '',
      resolved_by: ticket.resolved_by || '',
      status: ticket.status,
    });
  };

  const save = () => {
    const updates = {
      issue_description: form.issue_description,
      status: form.status,
    };
    if (form.resolved_by) updates.resolved_by = form.resolved_by;
    dispatch(updateCashierTicketThunk({ ticketId: editing.id, updates }))
      .unwrap()
      .then(() => setEditing(null))
      .catch((e) => alert(e.message || 'Update failed'));
  };

  return (
    <div className='index-box'>
      <Sidebar />
    <div style={{ padding: 24, width: '75%', margin: '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '2.5rem', fontWeight: 400 }}>Cashier Assistance Tickets</h2>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 8 }}>
          <option value="">All statuses</option>
          <option value="PENDING">Pending</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="RESOLVED">Resolved</option>
          <option value="FALSE_ALARM">False Alarm</option>
        </select>
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}
      {loading && tickets.length === 0 && <SkeletonList count={4} />}
      {!loading && tickets.length === 0 && <div style={{ color: '#6b7280' }}>No tickets found.</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {tickets.map((t) => (
          <div key={t.id}
               style={{ border: '1px solid #e5e7eb', borderLeft: `6px solid ${STATUS_COLORS[t.status] || '#999'}`,
                        borderRadius: 10, padding: '14px 18px', display: 'flex',
                        justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
            <div>
              <div style={{ fontWeight: 600 }}>
                #{t.id} — {t.store_name}
                <span style={{ marginLeft: 10, fontSize: 12, padding: '3px 10px', borderRadius: 999,
                               background: STATUS_COLORS[t.status], color: '#fff' }}>
                  {t.status.replace('_', ' ')}
                </span>
              </div>
              <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                Pressed {new Date(t.pressed_at).toLocaleString()}
                {t.resolved_by_name && <> · Resolved by <b>{t.resolved_by_name}</b>
                  {t.resolved_at && <> at {new Date(t.resolved_at).toLocaleTimeString()}</>}</>}
              </div>
              {t.issue_description && (
                <div style={{ marginTop: 6, fontSize: 14 }}>Issue: {t.issue_description}</div>
              )}
            </div>
            <button onClick={() => openEdit(t)}
                    style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db',
                             background: '#f9fafb', cursor: 'pointer' }}>
              Update
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 'min(480px, 92vw)' }}>
            <h3 style={{ marginTop: 0 }}>Update Ticket #{editing.id} — {editing.store_name}</h3>

            <label style={{ display: 'block', fontSize: 13, marginTop: 12 }}>What was the issue?</label>
            <textarea rows={3} value={form.issue_description}
                      onChange={(e) => setForm({ ...form, issue_description: e.target.value })}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }} />

            <label style={{ display: 'block', fontSize: 13, marginTop: 12 }}>Resolved by</label>
            <select value={form.resolved_by}
                    onChange={(e) => setForm({ ...form, resolved_by: e.target.value })}
                    style={{ width: '100%', padding: 10, borderRadius: 8 }}>
              <option value="">— me (default) —</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.user?.first_name} {emp.user?.last_name}
                </option>
              ))}
            </select>

            <label style={{ display: 'block', fontSize: 13, marginTop: 12 }}>Status</label>
            <select value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    style={{ width: '100%', padding: 10, borderRadius: 8 }}>
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="RESOLVED">Resolved</option>
              <option value="FALSE_ALARM">False Alarm</option>
            </select>

            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(null)}
                      style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #d1d5db',
                               background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={save}
                      style={{ padding: '9px 18px', borderRadius: 8, border: 0,
                               background: '#2563eb', color: '#fff', cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default CashierAssistancePage;